// Single chat message. Tap to progressively reveal: hz only → +pinyin → +translation.
// Tapping again at full hides everything. Words inside the message are tappable
// individually to save them to the library — only after pinyin is revealed, so
// the learner doesn't get vocab spoilers before they've tried reading.
//
// AI replies also get two extras on the hz line:
// 1. Library-saved words are highlighted (vermillion bold + yellow marker).
// 2. Untapped characters are clickable — tapping triggers a contextual lookup
//    that finds the natural word containing that character and offers Save.

import { useMemo, useRef, useState } from 'react';
import { Plus, Eye, EyeOff, Languages, Check, X, Loader2, Search } from 'lucide-react';
import type { ChatMessage, LibraryWord, Word } from '@/lib/types';
import { getAIClient } from '@/lib/ai/client';
import { Events, track } from '@/lib/observability';
import { C } from '@/styles/tokens';

type RevealStage = 'hidden' | 'pinyin' | 'full';

interface ChatBubbleProps {
  msg: ChatMessage;
  side: 'left' | 'right';
  library: LibraryWord[];
  onSaveWord: (word: Word) => void;
  /** Persona accent color — used to tint the user's own (right-side) bubble. */
  accent: string;
}

// Tap-to-save state machine. The "gated" stage exists so the user has to
// explicitly opt in to an AI lookup — taps without intent (e.g. brushing the
// hz to read it) shouldn't burn API budget. Cached results skip straight to
// "result" with no gate.
type LookupState =
  | { kind: 'gated'; charIndex: number; selection: string }
  | { kind: 'loading'; charIndex: number; selection: string }
  | { kind: 'result'; charIndex: number; selection: string; word: Word }
  | { kind: 'error'; charIndex: number; selection: string; message: string };

export function ChatBubble({ msg, side, library, onSaveWord, accent }: ChatBubbleProps) {
  const [stage, setStage] = useState<RevealStage>(side === 'right' ? 'full' : 'hidden');
  const [lookup, setLookup] = useState<LookupState | null>(null);
  // Cache resolved lookups for this bubble keyed by the tapped substring.
  // Re-taps return instantly with no API call. The cache is in a ref so
  // updates don't trigger re-renders; setLookup drives the UI separately.
  const lookupCache = useRef<Map<string, Word>>(new Map());
  const isMe = side === 'right';
  const showPinyin = stage !== 'hidden';
  const showEn = stage === 'full';

  const next = (): RevealStage => (stage === 'hidden' ? 'pinyin' : stage === 'pinyin' ? 'full' : 'hidden');
  const buttonLabel = stage === 'hidden' ? 'reveal' : stage === 'pinyin' ? 'translate' : 'hide';
  const ButtonIcon = stage === 'hidden' ? Eye : stage === 'pinyin' ? Languages : EyeOff;

  // Pre-compute the segment list once per (hz, library) pair. Library matches
  // become single highlighted units; everything else stays char-tappable.
  const segments = useMemo(
    () => (isMe ? null : segmentForHighlight(msg.hz, library)),
    [isMe, msg.hz, library],
  );

  function isSaved(w: Word): boolean {
    return library.some((l) => l.hz === w.hz);
  }

  function handleCharTap(charIndex: number) {
    const selection = msg.hz[charIndex] ?? '';
    const cached = lookupCache.current.get(selection);
    if (cached) {
      // Skip the gate — we already paid for this lookup in this bubble.
      setLookup({ kind: 'result', charIndex, selection, word: cached });
      return;
    }
    setLookup({ kind: 'gated', charIndex, selection });
  }

  async function handleConfirmLookup() {
    if (lookup?.kind !== 'gated') return;
    const { charIndex, selection } = lookup;
    setLookup({ kind: 'loading', charIndex, selection });
    try {
      const word = await getAIClient().lookup({
        context: msg.hz,
        selection,
      });
      // Cache by both the tapped substring and the resolved word so taps on
      // any character of the same compound hit the cache too.
      lookupCache.current.set(selection, word);
      lookupCache.current.set(word.hz, word);
      setLookup({ kind: 'result', charIndex, selection, word });
    } catch (err) {
      setLookup({
        kind: 'error',
        charIndex,
        selection,
        message: (err as Error).message ?? 'Lookup failed',
      });
    }
  }

  function handleSaveLookup() {
    if (lookup?.kind !== 'result') return;
    const isNew = !library.some((l) => l.hz === lookup.word.hz);
    onSaveWord(lookup.word);
    if (isNew) {
      track(Events.WordFirstEncountered, { hz: lookup.word.hz, source: 'chat' });
    }
    setLookup(null);
  }

  return (
    <div className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[78%] rounded-2xl px-4 py-2.5 ff-body text-sm ${isMe ? 'rounded-br-sm' : 'rounded-bl-sm'}`}
        style={{
          background: isMe ? accent : C.cream,
          color: isMe ? 'white' : C.ink,
          border: isMe ? `1px solid ${accent}` : `1px solid ${C.mist}`,
          boxShadow: isMe ? '0 1px 0 rgba(26,22,20,0.08)' : '0 1px 0 rgba(26,22,20,0.04), 0 8px 20px -10px rgba(120,90,70,0.10)',
        }}
      >
        <div className="ff-chat-hz text-[17px] leading-snug">
          {segments
            ? segments.map((s) =>
                s.kind === 'library' ? (
                  <span key={s.start} className="lib-highlight">
                    {s.text}
                  </span>
                ) : (
                  <button
                    key={`c${s.index}`}
                    type="button"
                    onClick={() => handleCharTap(s.index)}
                    className="inline align-baseline ff-chat-hz text-[17px] leading-snug bg-transparent p-0 border-0 cursor-pointer hover:bg-[rgba(26,22,20,0.05)] rounded-sm"
                    style={{ color: 'inherit' }}
                    aria-label={`Look up ${s.text}`}
                  >
                    {s.text}
                  </button>
                ),
              )
            : msg.hz}
        </div>

        {lookup && (
          <LookupCard
            state={lookup}
            existingIfSaved={
              lookup.kind === 'result' ? library.find((l) => l.hz === lookup.word.hz) : undefined
            }
            onConfirmLookup={handleConfirmLookup}
            onSave={handleSaveLookup}
            onClose={() => setLookup(null)}
          />
        )}

        {showPinyin && (
          <div className="mt-1.5 anim-slide-up">
            <div className="ff-body text-[11px] opacity-80">{msg.py}</div>
            {showEn && <div className="ff-body text-[12px] opacity-90 italic">{msg.en}</div>}
          </div>
        )}

        {!isMe && showPinyin && msg.words && msg.words.length > 0 && (
          <ul className="mt-2 flex flex-wrap gap-1.5 anim-slide-up" aria-label="Save these words">
            {msg.words.map((w) => {
              const saved = isSaved(w);
              return (
                <li key={w.hz}>
                  <button
                    type="button"
                    onClick={() => {
                      if (saved) return;
                      onSaveWord(w);
                      track(Events.WordFirstEncountered, { hz: w.hz, source: 'chat' });
                    }}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium"
                    style={{
                      background: saved ? C.blush : 'rgba(26,22,20,0.06)',
                      color: saved ? C.vermillionDeep : C.ink,
                    }}
                    aria-label={
                      saved ? `${w.hz} (${w.en}) already saved` : `Save ${w.hz} (${w.en}) to library`
                    }
                  >
                    {saved ? <Check size={11} aria-hidden /> : <Plus size={11} aria-hidden />}
                    <span className="ff-chat-hz">{w.hz}</span>
                    <span className="opacity-60">{w.py}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {!isMe && (
          <div className="mt-1 flex justify-end">
            <button
              type="button"
              onClick={() => setStage(next())}
              className="ff-body text-[10px] opacity-70 hover:opacity-100 inline-flex items-center gap-1"
              aria-label={
                stage === 'hidden'
                  ? 'Reveal pinyin'
                  : stage === 'pinyin'
                    ? 'Show translation'
                    : 'Hide pinyin and translation'
              }
            >
              <ButtonIcon size={10} aria-hidden />
              {buttonLabel}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function LookupCard({
  state,
  existingIfSaved,
  onConfirmLookup,
  onSave,
  onClose,
}: {
  state: LookupState;
  existingIfSaved: LibraryWord | undefined;
  onConfirmLookup: () => void;
  onSave: () => void;
  onClose: () => void;
}) {
  return (
    <div
      className="mt-2 p-2.5 rounded-xl anim-slide-up flex items-center gap-2"
      style={{ background: C.paper, border: `1px solid ${C.blush}` }}
      role="dialog"
      aria-label="Word lookup"
    >
      {state.kind === 'gated' && (
        <>
          <div className="flex-1 min-w-0 ff-body text-[12px]" style={{ color: C.inkLight }}>
            Look up{' '}
            <span className="ff-chat-hz text-[15px] font-bold" style={{ color: C.ink }}>
              {state.selection}
            </span>
            ?
          </div>
          <button
            type="button"
            onClick={onConfirmLookup}
            className="ff-body text-[11px] font-bold uppercase tracking-[0.15em] px-3 py-1.5 rounded-full inline-flex items-center gap-1"
            style={{ background: C.ink, color: 'white' }}
          >
            <Search size={11} aria-hidden /> Look up
          </button>
        </>
      )}

      {state.kind === 'loading' && (
        <span className="ff-body text-[12px] flex-1 inline-flex items-center gap-2" style={{ color: C.inkLight }}>
          <Loader2 size={12} className="animate-spin" aria-hidden /> Looking up…
        </span>
      )}

      {state.kind === 'error' && (
        <span className="ff-body text-[12px] flex-1" style={{ color: C.vermillionDeep }}>
          {state.message}
        </span>
      )}

      {state.kind === 'result' && (
        <>
          <div className="flex-1 min-w-0">
            <div className="ff-chat-hz text-[16px] font-bold leading-tight" style={{ color: C.ink }}>
              {state.word.hz}{' '}
              <span className="ff-body text-[11px] font-normal" style={{ color: C.inkLight }}>
                {state.word.py}
              </span>
            </div>
            <div className="ff-body text-[12px] truncate" style={{ color: C.inkLight }}>
              {state.word.en}
            </div>
          </div>
          {existingIfSaved ? (
            <span
              className="ff-body text-[10px] uppercase tracking-[0.15em] px-2 py-1 rounded-full"
              style={{ background: C.blush, color: C.vermillionDeep }}
            >
              Saved
            </span>
          ) : (
            <button
              type="button"
              onClick={onSave}
              className="ff-body text-[11px] font-bold uppercase tracking-[0.15em] px-3 py-1.5 rounded-full inline-flex items-center gap-1"
              style={{ background: C.ink, color: 'white' }}
            >
              <Plus size={11} aria-hidden /> Save
            </button>
          )}
        </>
      )}

      <button
        type="button"
        onClick={onClose}
        className="w-7 h-7 rounded-full flex items-center justify-center"
        aria-label="Close lookup"
        style={{ background: 'white' }}
      >
        <X size={12} aria-hidden />
      </button>
    </div>
  );
}

interface LibrarySegment {
  kind: 'library';
  text: string;
  start: number;
  length: number;
}

interface CharSegment {
  kind: 'char';
  text: string;
  index: number;
}

type Segment = LibrarySegment | CharSegment;

/**
 * Walk the hz string and produce a render plan: library matches collapse into
 * single highlighted spans; everything else stays as one-character tappable
 * spans for the contextual lookup flow.
 *
 * Library matching is greedy by length so that "咖啡" wins over a separate
 * "啡" entry, and earlier-starting matches win on ties.
 */
function segmentForHighlight(hz: string, library: LibraryWord[]): Segment[] {
  const taken = new Array(hz.length).fill(false) as boolean[];
  const sorted = [...library].sort((a, b) => b.hz.length - a.hz.length);
  const matches: { start: number; length: number; text: string }[] = [];

  for (const w of sorted) {
    if (!w.hz) continue;
    let from = 0;
    while (from <= hz.length - w.hz.length) {
      const idx = hz.indexOf(w.hz, from);
      if (idx === -1) break;
      let free = true;
      for (let k = idx; k < idx + w.hz.length; k++) {
        if (taken[k]) {
          free = false;
          break;
        }
      }
      if (free) {
        matches.push({ start: idx, length: w.hz.length, text: w.hz });
        for (let k = idx; k < idx + w.hz.length; k++) taken[k] = true;
      }
      from = idx + 1;
    }
  }

  matches.sort((a, b) => a.start - b.start);

  const segments: Segment[] = [];
  let i = 0;
  while (i < hz.length) {
    const m = matches.find((mm) => mm.start === i);
    if (m) {
      segments.push({ kind: 'library', text: m.text, start: m.start, length: m.length });
      i += m.length;
    } else {
      segments.push({ kind: 'char', text: hz[i] ?? '', index: i });
      i += 1;
    }
  }
  return segments;
}
